import {
  buildDemoSession,
  type AppSession,
  type DemoRole,
} from "../session/demo-session.js";
import {
  displayNameFromClaims,
  parseJwtPayload,
  tenantIdFromClaims,
} from "./parse-jwt.js";
import { getIdToken } from "./token-storage.js";

function roleFromGroups(groups: string[] | undefined): DemoRole {
  if (!groups || groups.length === 0) return "EMPLOYEE";
  const upper = groups.map((g) => g.toUpperCase());
  if (upper.some((g) => g.includes("HR") || g.includes("ADMIN") || g.includes("RH"))) {
    return "HR";
  }
  if (upper.some((g) => g.includes("MANAGER") || g.includes("GESTOR"))) {
    return "MANAGER";
  }
  return "EMPLOYEE";
}

/**
 * Monta AppSession a partir do access/id token (Cognito ou Supabase).
 * Permissões usam o mapa demo por papel até existir GET /me completo.
 */
export function buildSessionFromTokens(companyIdOverride?: string): AppSession | null {
  const idToken = getIdToken();
  if (!idToken) return null;
  const claims = parseJwtPayload(idToken);
  if (!claims) return null;

  const appMeta =
    claims.app_metadata && typeof claims.app_metadata === "object"
      ? (claims.app_metadata as Record<string, unknown>)
      : {};
  const userMeta =
    claims.user_metadata && typeof claims.user_metadata === "object"
      ? (claims.user_metadata as Record<string, unknown>)
      : {};

  const groupsRaw =
    claims["cognito:groups"] ?? appMeta.groups ?? userMeta.groups ?? appMeta.role;
  const groups = Array.isArray(groupsRaw)
    ? groupsRaw.filter((g): g is string => typeof g === "string")
    : typeof groupsRaw === "string"
      ? [groupsRaw]
      : undefined;

  const role = roleFromGroups(groups);
  const base = buildDemoSession(role);
  const tenantId = tenantIdFromClaims(claims);
  const email = typeof claims.email === "string" ? claims.email : base.email;
  const companies =
    tenantId != null ? [{ id: tenantId, name: "Homologação" }] : base.companies;
  const companyId =
    companyIdOverride && companies.some((c) => c.id === companyIdOverride)
      ? companyIdOverride
      : (companies[0]?.id ?? tenantId ?? "default");

  return {
    userId: typeof claims.sub === "string" ? claims.sub : base.userId,
    displayName: displayNameFromClaims(claims),
    email,
    role,
    permissions: base.permissions,
    companies,
    companyId,
  };
}
